import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, BookOpen, Search, Plus, Edit2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    account_code: '',
    account_name: '',
    parent_code: '',
    account_type: 'ACTIVO',
    nature: 'Deudora'
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = accounts.filter(account =>
        account.account_code.includes(searchTerm) ||
        account.account_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAccounts(filtered);
    } else {
      setFilteredAccounts(accounts);
    }
  }, [searchTerm, accounts]);

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data);
      setFilteredAccounts(response.data);
    } catch (error) {
      toast.error('Error al cargar cuentas');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/accounts/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar cuentas');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        ...formData,
        level: formData.account_code.length,
        is_active: true
      };

      if (editingAccount) {
        // Update existing account
        await axios.put(`${API}/accounts/${editingAccount.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cuenta actualizada correctamente');
      } else {
        // Create new account
        await axios.post(`${API}/accounts`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cuenta creada correctamente');
      }
      
      fetchAccounts();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar cuenta');
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      account_code: account.account_code,
      account_name: account.account_name,
      parent_code: account.parent_code || '',
      account_type: account.account_type,
      nature: account.nature
    });
    setDialogOpen(true);
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta cuenta? Esto puede afectar transacciones existentes.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cuenta eliminada correctamente');
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar cuenta');
    }
  };

  const resetForm = () => {
    setFormData({
      account_code: '',
      account_name: '',
      parent_code: '',
      account_type: 'ACTIVO',
      nature: 'Deudora'
    });
    setEditingAccount(null);
  };

  // Función para generar el siguiente código de cuenta automáticamente
  const generateAccountCode = (parentCode, accountType) => {
    if (!parentCode) {
      // Si no hay cuenta padre, buscar el siguiente código de nivel superior del tipo
      const baseDigit = {
        'ACTIVO': '1',
        'PASIVO': '2',
        'PATRIMONIO': '3',
        'INGRESOS': '4',
        'COSTOS': '5',
        'GASTOS': '6'
      }[accountType] || '1';
      
      // Buscar cuentas del mismo tipo que empiecen con el dígito base
      const sameLevelAccounts = accounts.filter(acc => 
        acc.account_code.startsWith(baseDigit) && 
        acc.parent_code === '' &&
        acc.account_code.length <= 5  // Nivel superior (máximo 5 dígitos)
      );
      
      if (sameLevelAccounts.length === 0) {
        // Primera cuenta de este tipo: generar código de 5 dígitos
        return baseDigit + '0101';
      }
      
      // Encontrar el código máximo y generar el siguiente
      const codes = sameLevelAccounts.map(acc => parseInt(acc.account_code) || 0);
      const maxCode = Math.max(...codes);
      const nextCode = maxCode + 1;
      
      // Asegurar que tenga al menos 5 dígitos
      return nextCode.toString().padStart(5, baseDigit + '0');
    }

    // Buscar todas las cuentas hijas de este padre
    const childAccounts = accounts.filter(acc => acc.parent_code === parentCode);
    
    if (childAccounts.length === 0) {
      // Primera cuenta hija: agregar "01" al código del padre
      return parentCode + '01';
    }

    // Encontrar el mayor número entre las cuentas hijas
    const childCodes = childAccounts.map(acc => {
      const suffix = acc.account_code.substring(parentCode.length);
      return parseInt(suffix) || 0;
    });
    
    const maxNumber = Math.max(...childCodes);
    const nextNumber = maxNumber + 1;
    
    // Formatear con ceros a la izquierda (2 dígitos)
    const paddedNumber = nextNumber.toString().padStart(2, '0');
    
    return parentCode + paddedNumber;
  };

  // Actualizar código automáticamente cuando cambia el padre o tipo
  const handleParentCodeChange = (newParentCode) => {
    setFormData(prev => {
      const newCode = generateAccountCode(newParentCode, prev.account_type);
      return {
        ...prev,
        parent_code: newParentCode,
        account_code: newCode
      };
    });
  };

  const handleAccountTypeChange = (newType) => {
    setFormData(prev => {
      const newCode = generateAccountCode(prev.parent_code, newType);
      return {
        ...prev,
        account_type: newType,
        account_code: newCode
      };
    });
  };

  const groupByType = (accounts) => {
    const grouped = {};
    accounts.forEach(account => {
      if (!grouped[account.account_type]) {
        grouped[account.account_type] = [];
      }
      grouped[account.account_type].push(account);
    });
    return grouped;
  };

  const accountsByType = groupByType(filteredAccounts);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="accounts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Catálogo de Cuentas</h1>
          <p className="text-slate-600 mt-1">Nomenclatura contable de Express Products</p>
        </div>
        <div className="flex items-center space-x-3">
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-account-button">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Cuenta
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingAccount ? 'Editar Cuenta' : 'Nueva Cuenta'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="account-form">
                <div className="space-y-2">
                  <Label htmlFor="account_type">Tipo de Cuenta *</Label>
                  <select
                    id="account_type"
                    value={formData.account_type}
                    onChange={(e) => handleAccountTypeChange(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    required
                    disabled={editingAccount !== null}
                    data-testid="account-type-select"
                  >
                    <option value="ACTIVO">ACTIVO (1)</option>
                    <option value="PASIVO">PASIVO (2)</option>
                    <option value="PATRIMONIO">PATRIMONIO (3)</option>
                    <option value="INGRESOS">INGRESOS (4)</option>
                    <option value="COSTOS">COSTOS (5)</option>
                    <option value="GASTOS">GASTOS (6)</option>
                  </select>
                  <p className="text-xs text-slate-500">Selecciona primero el tipo de cuenta</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="parent_code">Cuenta Padre (Opcional)</Label>
                  <Input
                    id="parent_code"
                    value={formData.parent_code}
                    onChange={(e) => handleParentCodeChange(e.target.value)}
                    placeholder="Ej: 401 (deja vacío para cuenta principal)"
                    data-testid="parent-code-input"
                    disabled={editingAccount !== null}
                  />
                  <p className="text-xs text-slate-500">Si especificas una cuenta padre, el código se genera automáticamente</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="account_code">Código de Cuenta (Auto-generado) *</Label>
                  <Input
                    id="account_code"
                    value={formData.account_code}
                    onChange={(e) => setFormData({ ...formData, account_code: e.target.value })}
                    placeholder="Se genera automáticamente"
                    required
                    disabled={editingAccount !== null}
                    data-testid="account-code-input"
                    className="bg-slate-50"
                  />
                  <p className="text-xs text-green-600">✓ Código generado automáticamente según tipo y padre</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="account_name">Nombre de la Cuenta *</Label>
                  <Input
                    id="account_name"
                    value={formData.account_name}
                    onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                    placeholder="Ej: VENTAS LOCALES"
                    required
                    data-testid="account-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nature">Naturaleza *</Label>
                  <select
                    id="nature"
                    value={formData.nature}
                    onChange={(e) => setFormData({ ...formData, nature: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    required
                    data-testid="nature-select"
                  >
                    <option value="Deudora">Deudora</option>
                    <option value="Acreedora">Acreedora</option>
                  </select>
                </div>

                <Button type="submit" className="w-full btn-primary" data-testid="save-account-button">
                  {editingAccount ? 'Actualizar' : 'Crear'} Cuenta
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <div className="relative">
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            <label htmlFor="file-upload">
              <Button
                as="span"
                variant="outline"
                className="cursor-pointer"
                disabled={uploading}
                data-testid="import-accounts-button"
              >
                {uploading ? (
                  <span className="flex items-center">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></span>
                    Importando...
                  </span>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Importar Excel
                  </>
                )}
              </Button>
            </label>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Buscar por código o nombre de cuenta..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-accounts-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Accounts by Type */}
      {Object.keys(accountsByType).length > 0 ? (
        Object.keys(accountsByType).map(type => (
          <Card key={type} className="hover-lift">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
                {type} ({accountsByType[type].length} cuentas)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full" data-testid={`accounts-table-${type}`}>
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Código</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Nombre</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Cuenta Mayor</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Nivel</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Naturaleza</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accountsByType[type].map(account => (
                      <tr key={account.id} className="border-b border-slate-100 table-row" data-testid="account-row">
                        <td className="py-3 px-4 font-mono text-sm text-slate-900 font-semibold">
                          {account.account_code}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-900">
                          {account.account_name}
                        </td>
                        <td className="py-3 px-4 font-mono text-sm text-slate-600">
                          {account.parent_code || '-'}
                        </td>
                        <td className="py-3 px-4 text-center text-sm text-slate-600">
                          {account.level}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            account.nature === 'Deudora'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {account.nature}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <Button
                              onClick={() => handleEdit(account)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:bg-blue-50"
                              data-testid="edit-account-button"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => handleDelete(account.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              data-testid="delete-account-button"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))
      ) : (
        <Card className="p-12 text-center">
          <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No hay cuentas registradas</h3>
          <p className="text-slate-600 mb-4">Importa el archivo Excel con el catálogo de cuentas o crea manualmente</p>
          <div className="flex items-center justify-center space-x-3">
            <Button onClick={() => setDialogOpen(true)} className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Crear Primera Cuenta
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Accounts;
